/**
 * Карточка звонка по номеру `/calls/phone/<номер>`: куда номер привязан (сделки, лиды, контакты —
 * тот же `GET /api/search`, что «Поиск»), все звонки номера (`GET /api/calls/phone/{номер}`) и
 * выбранный звонок целиком с «Кому дозванивались» (`?call=`, по умолчанию последний).
 * Сюда ведёт номер телефона из строки навигации слева сверху.
 * Спека `docs/specs/2026-10-20-call-route-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router";

import { fmtPhone, loadPhoneCalls, normPhone } from "@/lib/callRoute";
import type { CallItem } from "@/lib/calls";
import { loadSearch, type SearchResult } from "@/lib/search";
import { Card } from "@/pages/CallCardPage";
import { Outcome, TYPE } from "@/pages/CallsList";

const when = (at: string) => `${at.slice(8, 10)}.${at.slice(5, 7)}.${at.slice(2, 4)} ${at.slice(11, 16)}`;

function Binding({ s, calls }: { s: SearchResult; calls: CallItem[] }) {
  // Привязка самих звонков в Bitrix (`CRM_ENTITY_*` статистики) — сводкой по звонкам номера.
  const entities = [...new Set(calls.filter((c) => c.entity).map((c) => `${c.entity!.type.toLowerCase()} ${c.entity!.id ?? ""}`.trim()))];
  const empty = !s.deals.length && !s.leads.length && !s.contacts.length;
  return (
    <section className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px]" data-annot="привязка номера">
      <h2 className="text-[13px] font-medium">Куда привязан номер</h2>
      {s.main && (
        <div>
          Главный объект:{" "}
          <Link to={`/${s.main.entity === "deal" ? "deals" : "leads"}/${s.main.id}`} className="text-accent hover:underline">
            {s.main.entity === "deal" ? "сделка" : "лид"} {s.main.id}
          </Link>{" "}
          <span className="text-fg-muted">— {s.main.reason}</span>
        </div>
      )}
      {s.deals.map((d) => (
        <div key={`d${d.id}`} className={d.active ? "" : "text-fg-muted"}>
          <Link to={`/deals/${d.id}`} className="text-accent hover:underline">
            Сделка {d.id}
          </Link>
          {d.title && ` · ${d.title}`} · {d.stage_name ?? d.stage ?? "—"}
          {d.assigned && <span className="text-fg-muted"> · {d.assigned}</span>}
        </div>
      ))}
      {s.leads.map((l) => (
        <div key={`l${l.id}`} className={l.active ? "" : "text-fg-muted"}>
          <Link to={`/leads/${l.id}`} className="text-accent hover:underline">
            Лид {l.id}
          </Link>
          {l.title && ` · ${l.title}`} · {l.status_name}
          {l.assigned && <span className="text-fg-muted"> · {l.assigned}</span>}
        </div>
      ))}
      {s.contacts.map((c) => (
        <div key={`c${c.id}`} className="text-fg-muted">
          Контакт Bitrix {c.id} · {c.name}
        </div>
      ))}
      {empty && <div className="text-fg-faint">в Bitrix и CRM номер ни к чему не привязан</div>}
      {entities.length > 0 && <div className="text-fg-muted">Звонки в Bitrix привязаны к: {entities.join(", ")}</div>}
      {s.errors.length > 0 && <div className="text-warn">{s.errors.join(" · ")}</div>}
    </section>
  );
}

function CallsTable({ calls, picked, onPick }: { calls: CallItem[]; picked: number; onPick: (id: number) => void }) {
  return (
    <table className="w-full max-w-[64rem] text-[12.5px]" data-annot="звонки номера">
      <thead className="text-left text-[11px] text-fg-faint">
        <tr>
          <th className="py-1 pr-3 font-normal">Время</th>
          <th className="py-1 pr-3 font-normal">Тип</th>
          <th className="py-1 pr-3 font-normal">Сотрудник</th>
          <th className="py-1 pr-3 font-normal">Исход</th>
          <th className="py-1 pr-3 font-normal">Лид</th>
          <th className="py-1 pr-3 font-normal">Сделка</th>
          <th className="py-1 font-normal">Звонок</th>
        </tr>
      </thead>
      <tbody>
        {calls.map((c) => (
          <tr
            key={c.id}
            onClick={() => onPick(c.id)}
            className={`cursor-pointer border-t border-border hover:bg-bg-hover ${c.id === picked ? "bg-bg-hover font-medium" : ""}`}
          >
            <td className="py-1 pr-3 tabular-nums">{when(c.at)}</td>
            <td className="py-1 pr-3">{TYPE[c.type_id] ?? c.type_id}</td>
            <td className="py-1 pr-3">{c.user ?? "—"}</td>
            <td className="py-1 pr-3">
              <Outcome c={c} />
            </td>
            <td className="py-1 pr-3 tabular-nums">{c.lead_id ?? "—"}</td>
            <td className="py-1 pr-3 tabular-nums">{c.deal_id ?? "—"}</td>
            <td className="py-1 tabular-nums">
              <Link to={`/calls/${c.id}`} onClick={(e) => e.stopPropagation()} className="text-accent hover:underline">
                {c.id}
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CallPhonePage() {
  const { phone: raw = "" } = useParams();
  const phone = normPhone(raw);
  const [params, setParams] = useSearchParams();
  const calls = useQuery({ queryKey: ["phone-calls", phone], queryFn: () => loadPhoneCalls(phone), enabled: phone !== "" });
  const search = useQuery({ queryKey: ["quick-jump", phone], queryFn: () => loadSearch(phone), enabled: phone !== "" });
  const list = calls.data?.calls ?? [];
  const picked = Number(params.get("call")) || list[0]?.id || 0;
  const call = list.find((c) => c.id === picked);

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка номера">
        <Link to="/calls?tab=list" className="text-[12.5px] text-accent hover:underline">
          ← Звонки
        </Link>
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Звонки номера {phone ? fmtPhone(phone) : raw}</h1>
        {phone && (
          <Link to={`/search?q=${phone}`} className="text-[12.5px] text-accent hover:underline">
            поиск по номеру →
          </Link>
        )}
      </div>
      {!phone ? (
        <div className="text-[12.5px] text-err">Неверный номер: нужно 10–11 цифр.</div>
      ) : (
        <>
          {search.isPending ? (
            <div className="text-[12.5px] text-fg-muted">Ищу, куда привязан номер…</div>
          ) : search.isError ? (
            <div className="text-[12.5px] text-err">Привязку найти не удалось: {String(search.error.message)}</div>
          ) : (
            <Binding s={search.data} calls={list} />
          )}
          {calls.isPending ? (
            <div className="text-[12.5px] text-fg-muted">Загружаю звонки номера…</div>
          ) : calls.isError ? (
            <div className="text-[12.5px] text-err">Не удалось получить звонки: {String(calls.error.message)}</div>
          ) : list.length === 0 ? (
            <div className="text-[12.5px] text-fg-faint">Звонков с этим номером в копии нет.</div>
          ) : (
            <>
              <section className="flex flex-col gap-1.5">
                <h2 className="text-[13px] font-medium">
                  Звонки ({list.length}
                  {list.length >= (calls.data?.limit ?? 0) ? `, последние ${calls.data?.limit}` : ""})
                </h2>
                <CallsTable calls={list} picked={picked} onPick={(id) => setParams({ call: String(id) }, { replace: true })} />
              </section>
              {call && (
                <>
                  <h2 className="text-[14px] font-semibold">
                    <Link to={`/calls/${call.id}`} className="hover:underline">
                      Звонок {call.id}
                    </Link>{" "}
                    <span className="font-normal text-fg-muted">· {when(call.at)}</span>
                  </h2>
                  <Card c={call} />
                </>
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}
